import React from "react";
import { useSearchParams } from "react-router-dom";
import MarketingShell from "../components/MarketingShell";
import { DueCalculationExplainerBody } from "../components/DueCalculationExplainer.jsx";
import useCompanyStatementDocument from "../hooks/useCompanyStatementDocument.js";
import {
  AWARDED_NET_DAYS,
  CASH_RECEIVED_NET_DAYS,
  DEFAULT_SUCCESS_FEE_PERCENT,
  INTEREST_GRACE_DAYS,
  normalizeAccountStatementWebsite,
} from "../utils/accountsReceivable.js";

const SECTIONS = [
  {
    id: "success-fees",
    title: "How success fees are calculated",
    body: [
      `A success fee is only charged when an opportunity is awarded. The typical fee is ${DEFAULT_SUCCESS_FEE_PERCENT}% of the awarded amount. If a custom percentage or dollar fee was agreed for a line, that is what appears on the statement.`,
      "The awarded amount is the funding you won. The fee is our share of that award — not the full award.",
    ],
  },
  {
    id: "due-today",
    title: "Due today",
    body: [
      "Due today is the amount payable now. It includes unpaid installments and retainers whose date is today or earlier, plus their share of tax, plus any interest already accrued.",
      "The due date itself counts as due today. Payments already received are applied here first.",
    ],
  },
  {
    id: "coming-due-later",
    title: "Coming due later",
    body: [
      "Coming due later is the rest of the unpaid schedule — future payment-plan installments and retainers after today, plus their share of tax.",
      "Those amounts stay on the overall balance, but they are not part of what is payable today.",
    ],
  },
  {
    id: "balance-remaining",
    title: "Balance remaining",
    body: [
      "Balance remaining is the full outstanding amount: due today plus coming due later. Paying now reduces due today first, then coming due later.",
    ],
  },
  {
    id: "terms",
    title: "Payment terms",
    body: [
      `A success fee is due on the earlier of ${CASH_RECEIVED_NET_DAYS} days after cash is received from the funder, or ${AWARDED_NET_DAYS} days after the award date.`,
      "If there is no multi-month plan, the full unpaid fee is included in due today with this statement.",
    ],
  },
  {
    id: "plans",
    title: "Payment plans",
    body: [
      "A payment plan splits the fee into equal monthly installments starting on the awarded date. Each installment moves into due today on its scheduled date.",
    ],
  },
  {
    id: "interest",
    title: "Interest",
    body: [
      `Interest, if any, starts ${INTEREST_GRACE_DAYS} days after an amount’s due date at 1% per month. Interest is always included in due today.`,
    ],
  },
];

function GuideSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="h-24 rounded-xl border border-gray-100 bg-gray-100" />
        <div className="h-24 rounded-xl border border-gray-100 bg-gray-100" />
        <div className="h-24 rounded-xl border border-gray-100 bg-gray-100" />
      </div>
      <div className="space-y-3 rounded-xl border border-gray-100 p-6">
        <div className="h-4 w-1/3 rounded bg-gray-100" />
        <div className="h-3 w-2/3 rounded bg-gray-100" />
        <div className="h-32 rounded-lg bg-gray-100" />
      </div>
    </div>
  );
}

function GenericGuide({ scopedCompanyName }) {
  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        {scopedCompanyName
          ? `We could not load a live statement for ${scopedCompanyName}. This page is still the record of how due today, coming due later, and the success fee are calculated.`
          : "Your monthly account statement shows these amounts for your company, with a payment schedule for each opportunity. This page is the record of how those numbers are calculated."}
      </p>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-100 bg-white p-5">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Due today
          </p>
          <p className="mt-2 text-sm text-gray-600">What is payable now</p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-5">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Coming due later
          </p>
          <p className="mt-2 text-sm text-gray-600">Still on the schedule</p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-5">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Balance remaining
          </p>
          <p className="mt-2 text-sm text-gray-600">
            The full amount still outstanding
          </p>
        </div>
      </div>

      <div className="space-y-8 rounded-xl border border-gray-100 bg-white p-6 sm:p-8">
        <h2 className="text-base font-semibold text-gray-900">The record</h2>
        {SECTIONS.map((section) => (
          <section key={section.id} id={section.id} className="space-y-2">
            <h2 className="text-base font-semibold text-gray-900">
              {section.title}
            </h2>
            {section.body.map((paragraph) => (
              <p key={paragraph} className="text-sm leading-relaxed text-gray-600">
                {paragraph}
              </p>
            ))}
          </section>
        ))}
      </div>

      <p className="text-sm text-gray-500">
        Questions about a specific statement? Reply to the email — we can walk
        through the line items with you.
      </p>
    </div>
  );
}

export default function AccountStatementGuidePage() {
  const [searchParams] = useSearchParams();
  const website = normalizeAccountStatementWebsite(
    searchParams.get("website") || ""
  );
  const companyName = String(searchParams.get("company") || "").trim();
  const scoped = Boolean(website || companyName);
  const { loading, error, company, doc } = useCompanyStatementDocument({
    website,
    companyName,
  });

  const resolvedName =
    company?.fields?.Name || companyName || doc?.billTo?.name || "";

  if (!scoped) {
    return (
      <MarketingShell
        title="How success fees are calculated"
        description="A short guide to your monthly account statement — due today, coming due later, and how the fee is set."
      >
        <GenericGuide />
      </MarketingShell>
    );
  }

  if (loading) {
    return (
      <MarketingShell
        title="How your balance is calculated"
        description={
          resolvedName
            ? `${resolvedName} — loading this company's statement.`
            : "Loading this company's statement."
        }
      >
        <GuideSkeleton />
      </MarketingShell>
    );
  }

  if (error || !doc) {
    return (
      <MarketingShell
        title="How success fees are calculated"
        description="A short guide to your monthly account statement — due today, coming due later, and how the fee is set."
      >
        <GenericGuide scopedCompanyName={resolvedName || companyName} />
      </MarketingShell>
    );
  }

  return (
    <MarketingShell
      title="How your balance is calculated"
      description={`${resolvedName} — the same due today, coming due later, and payment schedule as your account statement.`}
    >
      <div className="space-y-6">
        <div className="rounded-xl border border-gray-100 bg-white p-6 sm:p-8">
          <DueCalculationExplainerBody doc={doc} showHeader={false} />
        </div>
        <p className="text-sm text-gray-500">
          Questions about this statement? Reply to the email — we can walk
          through the line items with you.
        </p>
      </div>
    </MarketingShell>
  );
}
